import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, canonicalUrl, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { AustinSiblingCallouts } from "./patio-covers.in.austin";
import hero from "@/assets/original/stock-curtains.jpg";
import heroPicture from "@/assets/original/stock-curtains.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";

const PATH = "/patio-enclosures/in/austin";
const TITLE = "Patio Enclosures in Austin, TX | Texas Made Shade";
const DESC = "Custom patio enclosures in Austin, TX — clear-vinyl drop curtains, Phifertex screen walls, motorized roll-downs. Turn your covered patio into a 3-season room. Free on-site quotes across Travis County.";

export const Route = createFileRoute("/patio-enclosures/in/austin")({
  head: (ctx) => ({
    links: [canonicalLink(PATH)],
    meta: routeMeta(ctx, {
      title: TITLE,
      description: DESC,
      image: hero,
      imageAlt: "Clear vinyl drop curtain patio enclosure installed in Austin",
      socialTitle: "Patio Enclosures in Austin, TX",
      socialDescription: DESC,
    }),
    scripts: [
      ...serviceHeadScripts({
        path: PATH,
        serviceName: "Patio Enclosures in Austin, TX",
        breadcrumbName: "Patio Enclosures in Austin",
        description: DESC,
        faqs: [
          { q: "What's the difference between clear vinyl and screen?", a: "Clear vinyl (20-gauge or 30-gauge marine vinyl) blocks wind and rain while staying see-through — best for Austin winter use and lake-wind exposure. Phifertex screen blocks bugs, sun and 90% UV while still letting breeze through — best for spring through fall." },
          { q: "Can you motorize the drop curtains?", a: "Yes — we install Somfy-powered motorized roll-down enclosures with wall-switch or app control. Common on larger Westlake, Lakeway and Barton Creek patios where you want to drop all four walls at once." },
          { q: "Will an enclosure work with my existing patio cover?", a: "Almost always. We mount tracks or zipper systems to existing wood, aluminum or stucco fascia. We come measure and confirm the fascia is structural before we quote." },
          { q: "Do you build screened-in porches in Austin?", a: "Yes — full Phifertex screen walls with zipper doors, framed in welded aluminum. We also rebuild aging wood screen porches with rust-proof aluminum framing." },
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Patio Enclosures in Austin, TX",
          serviceType: "Patio Enclosure Installation",
          url: canonicalUrl(PATH),
          provider: { "@id": "https://texasmadeshade.com/#business" },
          areaServed: { "@type": "City", name: "Austin", containedInPlace: { "@type": "AdministrativeArea", name: "Travis County" } },
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <ServicePage
      currentPath={PATH}
      serviceName="Patio Enclosures in Austin"
      breadcrumbs={[
        { label: "Patio Covers", to: "/patio-covers/in/austin" },
        { label: "Patio Enclosures in Austin, TX" },
      ]}
      eyebrow="★ PATIO ENCLOSURES · AUSTIN, TX ★"
      title="Custom Patio Enclosures in Austin, TX"
      intro="Turn your Austin covered patio into a 3-season room with custom drop curtains, screen walls and motorized roll-downs. Block wind, rain, mosquitos and cedar pollen without giving up the Hill Country view."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Drop Curtains & Screen Walls · Austin, TX",
        headline: "Patio Enclosure Quote — Free On-Site Measure",
        subhead: "Send photos of your covered patio with rough dimensions. We'll quote vinyl, screen and motorized options in one estimate.",
        primaryHref: `/#lets-talk?type=${encodeURIComponent("Drop Curtain")}&notes=${encodeURIComponent("Patio enclosure — Austin, TX.")}`,
        primaryLabel: "Get My Enclosure Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "20-gauge & 30-gauge clear marine vinyl drop curtains",
        "Phifertex Plus screen walls (90% UV block, bug-tight)",
        "Motorized Somfy roll-down systems",
        "Zipper, snap & track mounting options",
        "Fits existing wood, aluminum or stucco fascia",
        "Free on-site measure across Austin",
      ]}
      body={[
        {
          heading: "Three-Season Patios in a 4-Season City",
          text: "Austin winters are mild but windy off the Colorado River and Lake Travis, summers are full of mosquitos, and shoulder seasons are perfect — if you can keep the breeze without the bugs and cedar pollen. A custom enclosure makes your patio usable about 320 days a year instead of 180.",
        },
        {
          heading: "Vinyl, Screen or Both",
          text: "Most Austin patios end up with screen walls on the prevailing breeze side and clear vinyl drop curtains where lake winds drive weather in. We can also do hybrid panels — vinyl windows in a screen wall — sewn in our Seguin shop to fit the exact opening.",
          cta: { label: "Drop curtains & wind walls", href: "/curtains" },
        },
        {
          heading: "Motorized Roll-Downs for Large Openings",
          text: "Big covered patios (think 30-foot openings on Lakeway, Spicewood or Westlake builds) need motorized roll-downs — manual crank doesn't scale. We install Somfy-powered roll-downs with wall switches, remotes and optional wind sensors that auto-retract before a storm.",
          cta: { label: "Sun & wind curtains", href: "/curtains" },
        },
      ]}
      applications={[
        "Drop Curtain Patio Enclosures",
        "Screened-In Porches",
        "Motorized Roll-Down Walls",
        "Outdoor Kitchen Wind Walls",
        "Pool Cabana Enclosures",
        "Restaurant & Food-Truck Patios",
      ]}
      faqs={[
        { q: "Vinyl or screen?", a: "Vinyl blocks wind and rain — best for winter and lake exposure. Phifertex screen blocks bugs, sun and 90% UV with breeze — best March through November. Most Austin patios get both." },
        { q: "Can you motorize?", a: "Yes — Somfy-powered roll-downs with switch, remote or app control. Standard on larger covered patios." },
        { q: "Will it work with my current cover?", a: "Almost always. We mount to existing wood, aluminum or stucco fascia after confirming structure on the measure visit." },
        { q: "Do you build screened porches?", a: "Yes — full Phifertex screen walls with zipper doors framed in welded aluminum." },
      ]}
      cityCallouts={[
        { to: "/austin", city: "Austin", anchorText: "All Texas Made Shade services in Austin", blurb: "Every awning, cover, sail-shade and enclosure we install in Austin." },
        { to: "/curtains", city: "Statewide", anchorText: "Drop curtains & wind walls (statewide)", blurb: "See our full line of clear vinyl and screen drop curtains." },
      ]}
      postCta={<AustinSiblingCallouts current={PATH} />}
    />
  );
}
